import { cx } from './primitiveUtils';

/**
 * The top line of a statistics page: each figure under its label, in one panel
 * split by hairlines. See Apps/web/THEME.md, "The main pages", rule 4.
 *
 * Owner, 2026-10-07: the old summary cards (a gradient icon tile, a tinted glow
 * in the corner and a different colour per figure) read as an AI-made dashboard.
 * Here the label says what the number is, every figure is ink, and nothing is
 * decorated. Pass the values already formatted.
 */

export interface Figure {
  key: string;
  label: string;
  value: string;
  /** A short second line under the value, e.g. «120 زائر فريد». */
  note?: string;
}

export interface FigureStripProps {
  figures: readonly Figure[];
  /** Shows grey bars in place of the values while the figures load. */
  loading?: boolean;
  className?: string;
}

export default function FigureStrip({ figures, loading = false, className }: FigureStripProps) {
  return (
    // The negative end/bottom margins tuck the last column's and the last row's
    // hairlines under the rounded border, so only the lines between figures show,
    // however many columns the screen allows.
    <div className={cx('overflow-hidden rounded-[16px] border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface)]', className)}>
      <dl
        aria-busy={loading || undefined}
        className="-mb-px -me-px grid grid-cols-2 sm:grid-cols-3 lg:grid-flow-col lg:auto-cols-fr lg:grid-cols-none"
      >
        {figures.map((figure) => (
          <div key={figure.key} className="min-w-0 border-b border-e border-[var(--pv-border-subtle)] px-5 py-4">
            <dt className="truncate text-[0.875rem] text-[var(--pv-color-text-muted)]">{figure.label}</dt>
            {loading ? (
              <dd className="mt-2 h-7 w-16 animate-pulse rounded-md bg-[var(--pv-color-surface-sunken)] motion-reduce:animate-none" />
            ) : (
              <>
                <dd className="mt-1 text-[1.5rem] font-semibold leading-tight tabular-nums text-[var(--pv-color-text)]">
                  {figure.value}
                </dd>
                {figure.note && (
                  <dd className="mt-0.5 truncate text-[0.8125rem] text-[var(--pv-color-text-muted)]">{figure.note}</dd>
                )}
              </>
            )}
          </div>
        ))}
      </dl>
    </div>
  );
}
